<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">{{ title }}</h3>
        </div>
        <div class="card-toolbar">
          <button
            v-if="checkIfUserHasPermission(['ai-invoices-create'])"
            class="btn btn-light-primary font-weight-bolder"
            @click="showUploadModal"
          >
            <i class="flaticon2-file"></i>
            {{ $t("LABELS.UPLOAD_INVOICES") }}
          </button>
          <router-link
            :to="{ name: 'add-ai-invoices' }"
            class="btn btn-light-success font-weight-bolder ml-2"
            v-if="checkIfUserHasPermission(['ai-invoices-create'])"
          >
            <i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_MANUAL") }}
          </router-link>
        </div>
      </template>

      <v-alert
        v-if="hasNewInvoices"
        type="info"
        dismissible
        class="mx-6 mt-4"
        @input="hasNewInvoices = false"
      >
        <div class="d-flex align-items-center justify-content-between">
          <span>{{ $t("LABELS.NEW_INVOICES_ADDED") }}</span>
          <button
            @click="refreshTable"
            class="btn btn-sm btn-light-primary ml-3"
          >
            <i class="flaticon2-refresh"></i>
            {{ $t("LABELS.REFRESH") }}
          </button>
        </div>
      </v-alert>

      <v-card v-show="!isPageLoading">
        <InvoiceFilters
          :searchQuery.sync="searchQuery"
          :filters.sync="filters"
          :supplierOptions="supplierOptions"
          :buyerOptions="buyerOptions"
          :invoiceTypeOptions="invoiceTypeOptions"
          :showTypeFilter="showTypeFilter"
          @search="doSearch"
          @clear="doClear"
        />

        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :expanded.sync="expanded"
          show-expand
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          @update:options="getDataFromApi()"
          class="px-6 elevation-1"
        >
          <template v-slot:[`item.date`]="{ item }">
            <td :class="highlightRow(item)">
              <v-menu
                v-if="isEditingMain(item.id)"
                v-model="item.dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="item.date | formatDate"
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="item.date"
                  @input="
                    item.dateMenu = false;
                    markMainAsChanged(item);
                  "
                ></v-date-picker>
              </v-menu>
              <span v-else>{{ item.date | formatDate }}</span>
            </td>
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            <td :class="highlightRow(item)">
              {{ item.createdAt | formatDate }}
            </td>
          </template>

          <template v-slot:[`item.invoiceNumber`]="{ item }">
            <td
              :class="[
                highlightRow(item),
                item.isDuplicate ? 'duplicate-invoice' : ''
              ]"
            >
              <div class="d-flex align-items-center">
                <v-text-field
                  v-if="isEditingMain(item.id)"
                  v-model="item.invoiceNumber"
                  dense
                  hide-details
                  :class="item.isDuplicate ? 'duplicate-invoice' : ''"
                  @input="markMainAsChanged(item)"
                ></v-text-field>
                <span
                  v-else
                  :class="item.isDuplicate ? 'duplicate-invoice' : ''"
                >
                  {{ item.invoiceNumber }}
                </span>
                <v-tooltip v-if="item.isDuplicate" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="red"
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-content-copy
                    </v-icon>
                  </template>
                  <span>{{ $t("LABELS.DUPLICATE_INVOICE") }}</span>
                </v-tooltip>
              </div>
            </td>
          </template>

          <template v-slot:[`item.supplierName`]="{ item }">
            <td :class="highlightRow(item)">
              <v-autocomplete
                v-if="isEditingMain(item.id)"
                v-model="item.aiSupplierId"
                :items="supplierOptions"
                item-text="name"
                item-value="id"
                dense
                hide-details
                @change="markMainAsChanged(item)"
              ></v-autocomplete>
              <v-tooltip v-else bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span
                    :class="
                      item.supplierIsConfirmed ? 'text-primary' : 'text-danger'
                    "
                    v-bind="attrs"
                    v-on="on"
                    style="cursor: pointer"
                    @click="
                      showConfirmCompanyModal(
                        item.aiSupplierId,
                        'supplier',
                        item.supplierName
                      )
                    "
                  >
                    {{ item.supplierName }}
                    <v-icon
                      v-if="!item.supplierIsConfirmed"
                      small
                      color="red"
                      class="ml-1"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </span>
                </template>
                <span v-if="!item.supplierIsConfirmed">
                  {{ $t("LABELS.COMPANY_NOT_CONFIRMED") }} -
                  {{ $t("LABELS.CLICK_TO_CONFIRM") }}
                </span>
                <span v-else>
                  {{ $t("LABELS.EDIT_COMPANY") }}
                </span>
              </v-tooltip>
            </td>
          </template>

          <template v-slot:[`item.buyerName`]="{ item }">
            <td :class="highlightRow(item)">
              <v-autocomplete
                v-if="isEditingMain(item.id)"
                v-model="item.aiBuyerId"
                :items="buyerOptions"
                item-text="name"
                item-value="id"
                dense
                hide-details
                @change="markMainAsChanged(item)"
              ></v-autocomplete>
              <v-tooltip v-else bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span
                    :class="
                      item.buyerIsConfirmed ? 'text-primary' : 'text-danger'
                    "
                    v-bind="attrs"
                    v-on="on"
                    style="cursor: pointer"
                    @click="
                      showConfirmCompanyModal(
                        item.aiBuyerId,
                        'buyer',
                        item.buyerName
                      )
                    "
                  >
                    {{ item.buyerName }}
                    <v-icon
                      v-if="!item.buyerIsConfirmed"
                      small
                      color="red"
                      class="ml-1"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </span>
                </template>
                <span v-if="!item.buyerIsConfirmed">
                  {{ $t("LABELS.COMPANY_NOT_CONFIRMED") }} -
                  {{ $t("LABELS.CLICK_TO_CONFIRM") }}
                </span>
                <span v-else>
                  {{ $t("LABELS.EDIT_COMPANY") }}
                </span>
              </v-tooltip>
            </td>
          </template>

          <template v-slot:[`item.type`]="{ item }">
            <slot
              name="type-column"
              :item="item"
              :isEditingMain="isEditingMain"
              :markMainAsChanged="markMainAsChanged"
            >
              <td>
                <span>{{ item.type ? item.type.name : "-" }}</span>
              </td>
            </slot>
          </template>

          <template v-slot:[`item.totalAmount`]="{ item }">
            <v-text-field
              v-if="isEditingMain(item.id)"
              v-model.number="item.totalAmount"
              type="number"
              step="0.01"
              dense
              hide-details
              @input="markMainAsChanged(item)"
            ></v-text-field>
            <span v-else>{{ item.totalAmount }}</span>
          </template>

          <template v-slot:[`item.taxAmount`]="{ item }">
            <v-text-field
              v-if="isEditingMain(item.id)"
              v-model.number="item.taxAmount"
              type="number"
              step="0.01"
              dense
              hide-details
              @input="markMainAsChanged(item)"
            ></v-text-field>
            <span v-else>{{ item.taxAmount }}</span>
          </template>

          <template v-slot:[`item.currency`]="{ item }">
            <v-autocomplete
              v-if="isEditingMain(item.id)"
              v-model="item.currency"
              :items="currencyOptions"
              dense
              hide-details
              @change="markMainAsChanged(item)"
            ></v-autocomplete>
            <span v-else>{{ item.currency }}</span>
          </template>

          <template v-slot:[`item.paymentDeadlineDate`]="{ item }">
            <v-menu
              v-if="isEditingMain(item.id)"
              v-model="item.deadlineMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="item.paymentDeadlineDate | formatDate"
                  dense
                  hide-details
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="item.paymentDeadlineDate"
                @input="
                  item.deadlineMenu = false;
                  markMainAsChanged(item);
                "
              ></v-date-picker>
            </v-menu>
            <span v-else>{{ item.paymentDeadlineDate | formatDate }}</span>
          </template>

          <template v-slot:[`item.fileLink`]="{ item }">
            <td>
              <a
                v-if="getFileLink(item)"
                :href="getFileLink(item)"
                target="_blank"
                rel="noopener"
                class="file-link"
              >
                <v-icon small>mdi-link</v-icon>
                View
              </a>
              <span v-else>-</span>
            </td>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-if="
                    item.isTotalCorrect === false || item.isTotalCorrect === 0
                  "
                  small
                  color="red"
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert-circle
                </v-icon>
              </template>
              <span>{{ $t("LABELS.DATA_NOT_CORRECT") }}</span>
            </v-tooltip>
            <v-btn
              v-if="!isEditingMain(item.id)"
              icon
              small
              color="primary"
              @click="startMainEdit(item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <template v-else>
              <v-btn
                icon
                small
                color="success"
                class="mr-1"
                @click="saveMainEdit(item)"
                :loading="isSavingMain"
              >
                <v-icon small>mdi-content-save</v-icon>
              </v-btn>
              <v-btn icon small color="error" @click="cancelMainEdit(item)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
            <v-icon
              v-if="changedMainItems.includes(item.id)"
              small
              color="warning"
              class="ml-1"
            >
              mdi-circle
            </v-icon>
            <slot name="extra-actions" :item="item"></slot>
          </template>

          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <InvoiceRowsEditor
              :rows="item.rows"
              :isEditing="isEditing(item.id)"
              :isSaving="isSaving"
              :colspan="headers.length"
              :accountingValueTypes="
                accountingValueTypesMap[item.id] ||
                  effectiveAccountingValueTypes
              "
              :loadingAccountingValues="effectiveLoadingAccountingValues"
              @start-edit="startEdit(item)"
              @save-edit="saveEdit(item)"
              @cancel-edit="cancelEdit(item)"
              @add-row="addRow(item)"
              @delete-row="deleteRow(item, $event)"
              @update-row="
                updateRowField(item, $event, arguments[1], arguments[2])
              "
            />
          </template>
        </v-data-table>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>

    <b-modal
      ref="uploadModal"
      :title="$t('LABELS.UPLOAD_INVOICES')"
      hide-footer
      size="lg"
      @hidden="onModalHidden"
    >
      <AiInvoiceUploadForm @uploaded="onUploaded" />
    </b-modal>

    <b-modal
      ref="confirmCompanyModal"
      :title="$t('LABELS.CONFIRM_COMPANY')"
      @ok="confirmCompany"
      @hidden="resetConfirmModal"
      ok-variant="success"
      :ok-title="$t('LABELS.CONFIRM')"
      :cancel-title="$t('LABELS.CANCEL')"
    >
      <div v-if="confirmModalData.loading" class="text-center my-4">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <div v-else>
        <p>
          {{
            $t("LABELS.CONFIRM_COMPANY_MESSAGE", {
              type:
                confirmModalData.type === "supplier"
                  ? $t("LABELS.SUPPLIER")
                  : $t("LABELS.BUYER"),
              name: confirmModalData.companyName
            })
          }}
        </p>
        <AiCompanyForm
          v-if="confirmModalData.companyForm"
          :form="confirmModalData.companyForm"
          :errors="confirmModalData.error ? [confirmModalData.error] : []"
          ref="companyForm"
        />
        <v-alert v-if="confirmModalData.error" type="error" class="mt-3">
          {{ confirmModalData.error }}
        </v-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import InvoiceFilters from "./InvoiceFilters.vue";
import InvoiceRowsEditor from "./InvoiceRowsEditor.vue";
import AiInvoiceUploadForm from "./AiInvoiceUploadForm.vue";
import AiCompanyForm from "@/modules/ai-companies/components/AiCompanyForm.vue";
import invoiceEditMixin from "../mixins/invoiceEditMixin.js";
import {
  FETCH_AI_COMPANY,
  UPDATE_AI_COMPANY,
  FETCH_AI_COMPANY_OPTIONS
} from "@/modules/ai-companies/store/ai-companies.module";
import {
  FETCH_AI_INVOICES,
  UPDATE_AI_INVOICES_BATCH,
  UPDATE_AI_INVOICE_ROWS,
  UPDATE_AI_INVOICE
} from "@/modules/ai-invoices/store/ai-invoices.module";
import { FETCH_AI_INVOICE_TYPE_ACCOUNTING_VALUE_TYPES } from "@/modules/ai-Invoice-accounting-value-types/store/ai-invoice-accounting-value-types.module";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";

export default {
  name: "InvoiceListBase",
  mixins: [permissionMixin, invoiceEditMixin],
  components: {
    InvoiceFilters,
    InvoiceRowsEditor,
    AiInvoiceUploadForm,
    AiCompanyForm
  },
  props: {
    title: {
      type: String,
      required: true
    },
    showTypeFilter: {
      type: Boolean,
      default: false
    },
    invoiceTypeOptions: {
      type: Array,
      default: () => []
    },
    invoiceTypeId: {
      type: Number,
      default: null
    },
    additionalHeaders: {
      type: Array,
      default: () => []
    },
    getCurrentInvoiceCount: {
      type: Function,
      required: true
    },
    accountingValueTypes: {
      type: Array,
      default: null
    },
    loadingAccountingValues: {
      type: Boolean,
      default: null
    },
    accountingValueTypesMap: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isPageLoading: true,
      items: [],
      expanded: [],
      totalItems: 100,
      searchQuery: "",
      currencyOptions: ["BGN", "EUR", "USD", "GBP"],
      options: {
        sortBy: ["id"],
        sortDesc: [true],
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      filters: {
        dateFrom: null,
        dateTo: null,
        aiSupplierId: null,
        aiBuyerId: null,
        aiInvoiceTypeId: null,
        isProcessed: false
      },
      supplierOptions: [],
      buyerOptions: [],
      rowsPerPageItems: [10, 20, 30, 40, 50],
      countCheckInterval: null,
      lastKnownCount: 0,
      hasNewInvoices: false,
      confirmModalData: {
        companyId: null,
        type: null,
        companyName: "",
        error: "",
        loading: false,
        companyForm: null
      },
      internalAccountingValueTypes: [],
      internalLoadingAccountingValues: false
    };
  },
  computed: {
    params() {
      return {
        ...this.options,
        ...this.filters,
        query: this.searchQuery,
        ...(this.invoiceTypeId && { aiInvoiceTypeId: this.invoiceTypeId })
      };
    },
    tableHeaders() {
      const baseHeaders = [
        { text: this.$t("FORM_LABELS.DATE"), value: "date" },
        { text: this.$i18n.t("FORM_LABELS.CREATED_AT"), value: "createdAt" },
        { text: this.$t("FORM_LABELS.INVOICE_NUMBER"), value: "invoiceNumber" },
        { text: this.$t("FORM_LABELS.SUPPLIER"), value: "supplierName" },
        { text: this.$t("FORM_LABELS.BUYER"), value: "buyerName" }
      ];

      const endHeaders = [
        {
          text: this.$t("FORM_LABELS.TOTAL_WITHOUT_VAT"),
          value: "totalWithoutVat"
        },
        { text: this.$t("FORM_LABELS.TAX_AMOUNT"), value: "taxAmount" },
        { text: this.$t("FORM_LABELS.TOTAL_AMOUNT"), value: "totalAmount" },
        { text: this.$t("FORM_LABELS.CURRENCY"), value: "currency" },
        {
          text: this.$t("FORM_LABELS.PAYMENT_DEADLINE"),
          value: "paymentDeadlineDate"
        },
        { text: this.$t("FORM_LABELS.LINK"), value: "fileLink" },
        {
          align: "right",
          text: this.$t("MENU.ITEM.ACTIONS"),
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ];

      return [...baseHeaders, ...this.additionalHeaders, ...endHeaders];
    },
    highlightedInvoiceId() {
      return this.$route.query.highlight;
    },
    effectiveAccountingValueTypes() {
      return this.accountingValueTypes !== null
        ? this.accountingValueTypes
        : this.internalAccountingValueTypes;
    },
    effectiveLoadingAccountingValues() {
      return this.loadingAccountingValues !== null
        ? this.loadingAccountingValues
        : this.internalLoadingAccountingValues;
    }
  },
  watch: {
    expanded(newVal, oldVal) {
      const newlyExpanded = newVal.filter(item => !oldVal.includes(item));
      const newlyCollapsed = oldVal.filter(item => !newVal.includes(item));

      newlyExpanded.forEach(item => {
        this.$emit("item-expanded", { item, value: true });
      });

      newlyCollapsed.forEach(item => {
        this.$emit("item-expanded", { item, value: false });
      });
    }
  },
  async mounted() {
    this.fetchSuppliers();
    this.fetchBuyers();
    if (this.invoiceTypeId && this.accountingValueTypes === null) {
      await this.fetchAccountingValueTypes();
    }
    await this.getDataFromApi();
    try {
      this.lastKnownCount = await this.getInvoiceCount();
    } catch (error) {
      console.error("Error initializing invoice count:", error);
    }
    this.startCountCheck();
  },
  beforeDestroy() {
    this.stopCountCheck();
  },
  methods: {
    async fetchSuppliers() {
      const data = await this.$store.dispatch(
        FETCH_AI_COMPANY_OPTIONS,
        "?is_buyer=false"
      );
      this.supplierOptions = data;
    },
    async fetchBuyers() {
      const data = await this.$store.dispatch(
        FETCH_AI_COMPANY_OPTIONS,
        "?is_buyer=true"
      );
      this.buyerOptions = data;
    },
    async fetchAccountingValueTypes() {
      if (!this.invoiceTypeId) return;

      this.internalLoadingAccountingValues = true;
      try {
        const data = await this.$store.dispatch(
          FETCH_AI_INVOICE_TYPE_ACCOUNTING_VALUE_TYPES,
          this.invoiceTypeId
        );
        this.internalAccountingValueTypes = data.data;
      } catch (error) {
        console.error("Error fetching accounting value types:", error);
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("LABELS.ERROR_LOADING_ACCOUNTING_VALUES")
        });
      } finally {
        this.internalLoadingAccountingValues = false;
      }
    },
    showUploadModal() {
      this.$refs.uploadModal.show();
    },
    async onUploaded() {
      this.$refs.uploadModal.hide();
      this.$notify({
        group: "notify",
        type: "info",
        text: this.$t("LABELS.INVOICES_PROCESSING"),
        duration: 5000
      });
      this.lastKnownCount = await this.getInvoiceCount();
      this.startCountCheck();
    },
    startCountCheck() {
      if (this.countCheckInterval) clearInterval(this.countCheckInterval);
      this.countCheckInterval = setInterval(() => {
        this.checkInvoiceCount();
      }, 5000);
    },
    stopCountCheck() {
      if (this.countCheckInterval) {
        clearInterval(this.countCheckInterval);
        this.countCheckInterval = null;
      }
    },
    async getInvoiceCount() {
      if (!this.getCurrentInvoiceCount) {
        console.warn("getCurrentInvoiceCount prop is not defined");
        return 0;
      }
      return await this.getCurrentInvoiceCount();
    },
    async checkInvoiceCount() {
      try {
        const currentCount = await this.getInvoiceCount();

        if (this.isPageLoading) {
          return;
        }

        if (this.lastKnownCount === 0) {
          this.lastKnownCount = currentCount;
          return;
        }

        if (currentCount > this.lastKnownCount) {
          this.hasNewInvoices = true;
          this.lastKnownCount = currentCount;
        }
      } catch (error) {
        console.error("Error checking invoice count:", error);
      }
    },
    async refreshTable() {
      this.hasNewInvoices = false;
      await this.getDataFromApi();
      await this.fetchSuppliers();
      await this.fetchBuyers();
      this.lastKnownCount = await this.getInvoiceCount();
    },
    doSearch() {
      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    doClear() {
      this.searchQuery = "";
      this.filters.dateFrom = null;
      this.filters.dateTo = null;
      this.filters.aiSupplierId = null;
      this.filters.aiBuyerId = null;
      this.filters.aiInvoiceTypeId = null;
      this.filters.isProcessed = false;
      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    getDataFromApi() {
      let vm = this;
      let params = this.params;
      params.length = params.itemsPerPage;
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1);

      if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
      if (this.filters.dateTo) params.dateTo = this.filters.dateTo;
      if (this.filters.aiSupplierId)
        params.aiSupplierId = this.filters.aiSupplierId;
      if (this.filters.aiBuyerId) params.aiBuyerId = this.filters.aiBuyerId;
      if (this.filters.aiInvoiceTypeId)
        params.aiInvoiceTypeId = this.filters.aiInvoiceTypeId;

      let apiParams = vm.$url.transformParams(params);
      vm.$store
        .dispatch(FETCH_AI_INVOICES, apiParams)
        .then(data => {
          vm.$nextTick(function() {
            vm.items = data.items || data.data?.items || [];
            const newTotal =
              data.totalItemsCount || data.data?.totalItemsCount || 0;
            vm.totalItems = newTotal;

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: this.$t("VALIDATION.NOT_FOUND", {
                name: this.$t("MENU.ITEM.EXP.AI_INVOICES")
              })
            });
            this.$router.push({ name: "list-ai-invoices" });
          }
        });
    },
    highlightRow(item) {
      return item.id && item.id.toString() === this.highlightedInvoiceId
        ? "highlighted-row"
        : "";
    },
    getFileLink(item) {
      return item.fileLink || item.gdriveLink;
    },
    async saveMainEdit(item) {
      this.isSavingMain = true;
      try {
        const payload = this.buildMainEditPayload(item);
        await this.$store.dispatch(UPDATE_AI_INVOICE, {
          id: item.id,
          payload: payload
        });

        this.$notify({
          group: "notify",
          type: "success",
          text: this.$t("LABELS.ALL_CHANGES_SAVED_SUCCESS")
        });

        this.editingMainInvoiceId = null;
        this.originalMainData = null;
        const changedIndex = this.changedMainItems.indexOf(item.id);
        if (changedIndex > -1) {
          this.changedMainItems.splice(changedIndex, 1);
        }

        await this.getDataFromApi();
      } catch (error) {
        console.error(error);
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("LABELS.ERROR_SAVING_COMPANY")
        });
      } finally {
        this.isSavingMain = false;
      }
    },
    async saveMainChanges() {
      this.isSavingMain = true;
      try {
        const changedInvoices = this.items
          .filter(item => this.changedMainItems.includes(item.id))
          .map(item => this.buildMainEditPayload(item));

        await this.$store.dispatch(UPDATE_AI_INVOICES_BATCH, {
          invoices: changedInvoices
        });

        this.$notify({
          group: "notify",
          type: "success",
          text: this.$t("LABELS.ALL_INVOICES_PROCESSED")
        });

        this.changedMainItems = [];
        this.editingMainInvoiceId = null;
        this.originalMainData = null;
        this.getDataFromApi();
      } catch (error) {
        console.error(error);
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("LABELS.UPLOAD_FAILED")
        });
      } finally {
        this.isSavingMain = false;
      }
    },
    buildMainEditPayload(item) {
      return {
        id: item.id,
        date: item.date,
        invoiceNumber: item.invoiceNumber,
        totalAmount: item.totalAmount,
        taxAmount: item.taxAmount,
        currency: item.currency,
        paymentDeadlineDate: item.paymentDeadlineDate,
        aiSupplierId: item.aiSupplierId,
        aiBuyerId: item.aiBuyerId,
        ...(item.aiInvoiceTypeId && { aiInvoiceTypeId: item.aiInvoiceTypeId })
      };
    },
    async saveEdit(item) {
      this.isSaving = true;
      try {
        const payload = {
          rows: item.rows.map(r => ({
            id: r.id,
            itemName: r.itemName,
            itemQuantity: r.itemQuantity,
            itemPrice: r.itemPrice,
            itemDiscountPercentage: r.itemDiscountPercentage || 0,
            itemTotalPrice: r.itemTotalPrice,
            itemRowNumber: r.itemRowNumber,
            aiInvoiceAccountingValueId: r.aiInvoiceAccountingValueId
          })),
          deletedRowIds: this.deletedRowIds
        };

        await this.$store.dispatch(UPDATE_AI_INVOICE_ROWS, {
          invoiceId: item.id,
          payload: payload
        });

        this.$notify({
          group: "notify",
          type: "success",
          text: this.$t("LABELS.COMPLETED")
        });

        this.editingInvoiceId = null;
        this.originalRows = null;
        this.deletedRowIds = [];
        this.getDataFromApi();
      } catch (error) {
        console.error(error);
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("LABELS.UPLOAD_FAILED")
        });
      } finally {
        this.isSaving = false;
      }
    },
    showConfirmCompanyModal(companyId, type, companyName) {
      this.confirmModalData = {
        companyId: companyId,
        type: type,
        companyName: companyName,
        error: "",
        loading: true,
        companyForm: null
      };
      this.$refs.confirmCompanyModal.show();
      this.$store
        .dispatch(FETCH_AI_COMPANY, companyId)
        .then(data => {
          const form = {
            ...data.data,
            isConfirmed: true,
            isBuyer: Boolean(data.data.isBuyer),
            isSupplier: Boolean(data.data.isSupplier)
          };
          this.confirmModalData.companyForm = form;
          this.confirmModalData.loading = false;
        })
        .catch(() => {
          this.confirmModalData.error = this.$t("LABELS.COMPANY_CONFIRM_ERROR");
          this.confirmModalData.loading = false;
        });
    },
    async confirmCompany(evt) {
      if (
        !this.confirmModalData.companyId ||
        !this.confirmModalData.companyForm
      )
        return;
      this.confirmModalData.error = "";
      this.confirmModalData.loading = true;
      try {
        await this.$store.dispatch(UPDATE_AI_COMPANY, {
          id: this.confirmModalData.companyId,
          payload: this.confirmModalData.companyForm
        });
        this.$notify({
          group: "notify",
          type: "success",
          text: this.$t("LABELS.COMPANY_CONFIRMED_SUCCESS")
        });
        await this.getDataFromApi();
        this.$refs.confirmCompanyModal.hide();
      } catch (error) {
        this.confirmModalData.error =
          error?.data?.message || this.$t("LABELS.COMPANY_CONFIRM_ERROR");
      } finally {
        this.confirmModalData.loading = false;
      }
    },
    resetConfirmModal() {
      this.confirmModalData = {
        companyId: null,
        type: null,
        companyName: "",
        error: "",
        loading: false,
        companyForm: null
      };
    }
  }
};
</script>

<style scoped>
td.highlighted-row {
  color: #44f005;
}

.duplicate-invoice {
  color: #e53935 !important;
  font-weight: bold;
}

.v-text-field {
  min-width: 100px;
}

.file-link {
  text-decoration: none;
  color: #1976d2;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.file-link:hover {
  text-decoration: underline;
}
</style>
